/**
 * Reusable Pagination Component
 */

import type { ReactNode } from "react";
import { LeadingIcon, TrailingIcon } from "@/assets/svg";

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  previousButton?: ReactNode;
  nextButton?: ReactNode;
}

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  previousButton = (
    <>
      <LeadingIcon /> Previous
    </>
  ),
  nextButton = (
    <>
      Next <TrailingIcon />
    </>
  ),
}: PaginationProps) => {
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  return (
    <div className="flex justify-center items-center gap-3 mt-4 max-md:gap-1">
      <button
        className="flex items-center gap-2 py-2.5 px-4 border-0 bg-transparent text-slate-900 rounded-md cursor-pointer text-sm font-semibold transition-all duration-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed max-md:py-1.5 max-md:px-2.5 max-md:text-[13px]"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        {previousButton || "Previous"}
      </button>

      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
        if (
          page === 1 ||
          page === totalPages ||
          (page >= currentPage - 1 && page <= currentPage + 1)
        ) {
          return (
            <button
              key={page}
              className={`py-2 px-3.5 border-0 rounded-lg cursor-pointer text-base font-medium min-w-[40px] transition-all duration-200 max-md:py-1.5 max-md:px-2.5 max-md:text-[13px] ${
                page === currentPage 
                  ? "bg-slate-900 text-white hover:bg-slate-800" 
                  : "bg-transparent text-slate-900 hover:bg-slate-100"
              }`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          );
        } else if (page === currentPage - 2 || page === currentPage + 2) {
          return (
            <span key={page} className="py-2 px-1 font-medium text-base leading-6 tracking-[0%] text-center">
              ...
            </span>
          );
        }
        return null;
      })}

      <button
        className="flex items-center gap-2 py-2.5 px-4 border-0 bg-transparent text-slate-900 rounded-md cursor-pointer text-sm font-semibold transition-all duration-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed max-md:py-1.5 max-md:px-2.5 max-md:text-[13px]"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        {nextButton || "Next"}
      </button>
    </div>
  );
};
